import { Component, Input } from '@angular/core';
import {
  NameResolveService,
  ResolveArrangement,
  ResolveResponse,
} from 'src/app/services/name-resolve-service';
import { Router } from '@angular/router';
import { CharCell, gbp, plateChars } from '../plate-chars';

/* The Finder (process=names-resolve). Self-contained: input, three verdicts
   with reason codes, loading/empty/error, aria-live. The parent fills it from
   an Out-of-Reach card via the public look() method. */
@Component({
  selector: 'pla-name-finder',
  templateUrl: './name-finder.component.html',
  styleUrls: ['./name-finder.component.scss'],
})
export class NameFinderComponent {
  @Input() reducedMotion = false;

  public term = '';
  public state: 'idle' | 'loading' | 'result' | 'error' = 'idle';
  public result: ResolveResponse | null = null;
  public suggestions = ['James', 'Matilda', 'Jack', 'Yvonne', 'Christopher', 'Zoe'];
  private seq = 0;
  private debounce: any = null;

  constructor(private resolveService: NameResolveService, private router: Router) {}

  public onInput(value: string): void {
    this.term = value;
    if (this.debounce) clearTimeout(this.debounce);
    if (!value.trim()) {
      this.state = 'idle';
      this.result = null;
      return;
    }
    this.debounce = setTimeout(() => this.run(), 260);
  }

  public run(term?: string): void {
    if (term !== undefined) this.term = term;
    const q = (this.term || '').trim();
    if (!q) {
      this.state = 'idle';
      this.result = null;
      return;
    }
    const seq = ++this.seq;
    this.state = 'loading';
    this.resolveService.resolve(q).subscribe(
      (res: ResolveResponse) => {
        if (seq !== this.seq) return;
        this.result = res;
        this.state = 'result';
        this.emitAnalytics(res);
      },
      () => {
        if (seq !== this.seq) return;
        // an API failure must NOT read as "cannot be written"
        this.result = null;
        this.state = 'error';
      }
    );
  }

  /** Fill and run the Finder (called by the parent's Out-of-Reach cards). */
  public look(name: string): void {
    this.run(name);
  }

  public chars(display: string): CharCell[] {
    return plateChars(display);
  }

  public blockedLetters(): string {
    return (this.result?.reason?.detail?.letters || []).join(', ');
  }

  public arrangementState(a: ResolveArrangement): string {
    const price = gbp(a.pricePence);
    if (a.state === 'new-issue') return price ? `New issue · ${price}` : 'New issue · DVLA';
    if (a.state === 'resale') return price ? `For sale · from ${price}` : 'For sale';
    if (a.state === 'held') return 'Held';
    if (a.state === 'unknown') return 'Availability unknown';
    if (a.existence === 'no-record') return 'No record of issue';
    return 'Previously sold';
  }

  public plateLink(plain: string): string[] {
    return ['/plate', plain.replace(/\s+/g, '')];
  }

  /** Onward discovery: search the normal PlateX search for this name (#6/#7). */
  public searchName(): void {
    const term = this.result?.searchTerm || (this.term || '').trim();
    if (term) this.router.navigate(['/search-results', term]);
  }

  public get searchLabel(): string {
    const t = this.result?.input?.normalised || this.term;
    const nice = t ? t.charAt(0) + t.slice(1).toLowerCase() : 'these';
    return `See available ${nice} plates`;
  }

  private emitAnalytics(res: ResolveResponse): void {
    const inRegister = !!res.arrangements && res.arrangements.some((a) => a.inRegister);
    try {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'names_finder_query',
        query: res.input?.normalised,
        verdict: res.verdict || res.outcome,
        inRegister,
      });
    } catch (e) {
      /* analytics must never break the Finder */
    }
  }
}
